import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ProjectPhase } from 'api/project';
import { TaskType, TaskUpdatePhasePayload } from 'api/tasks';
import { toastService } from 'components/molecules/toast';
import { Task } from 'data/tasks';
import {
  deleteTaskSagaAction,
  updateTaskPrivacySagaAction,
  updateTaskSagaAction,
} from 'data/tasks/actions';
import { useDeleteConfirmModal } from 'hooks/use-delete-confirm-modal';
import { useTranslate } from 'hooks/use-translation';
import { useUpdatePhaseTaskMutation } from 'queries/task';
import { isTaskDatesOutOfPhaseDateRange } from './utils';

type MoveTaskState = {
  isModalVisible;
  projectPhase?: ProjectPhase | null;
};

export const useTaskMoreActions = ({
  data,
  setTaskValue,
}: {
  data: Task;
  setTaskValue?: React.Dispatch<React.SetStateAction<Task>>;
}) => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const {
    project,
    taskId,
    type,
    private: isPrivate,
    atRisk,
    startDate,
    dueDate,
  } = data;
  const projectId = project;
  const { mutateAsync: updateTaskPhase, isLoading: isUpdateTaskPhaseLoading } =
    useUpdatePhaseTaskMutation(taskId);
  const [moveTaskState, setMoveTaskState] = useState<MoveTaskState>({
    isModalVisible: false,
    projectPhase: null,
  });
  const handleConfirmDelete = useCallback(() => {
    dispatch(deleteTaskSagaAction({ projectId, taskId }));
  }, [dispatch, projectId, taskId]);

  const deleteConfirmation = useDeleteConfirmModal({
    title: translate('task.delete.confirm_title'),
    content: translate('task.delete.confirm_message'),
    onOk: () => handleConfirmDelete(),
  });

  const handleMoveTaskCancel = useCallback(() => {
    setMoveTaskState((prevState) => ({
      ...prevState,
      isModalVisible: false,
      projectPhase: null,
    }));
  }, []);

  const handleMoveTaskSubmit = useCallback(
    async (payload: TaskUpdatePhasePayload) => {
      return updateTaskPhase(payload, {
        onSuccess: (res) => {
          setMoveTaskState((prevState) => ({
            ...prevState,
            isModalVisible: false,
            projectPhase: null,
          }));
          toastService({
            title: translate('task.move_to_phase.success.title'),
            message: translate('task.move_to_phase.success.message', {
              phaseName: res.projectPhase?.projectPhaseName ?? '',
            }),
          }).success();
        },
      });
    },
    [updateTaskPhase, translate],
  );

  const handleMarkAtRisk = useCallback(() => {
    dispatch(
      updateTaskSagaAction({
        projectId,
        taskId,
        data: { atRisk: !atRisk },
        options: setTaskValue
          ? { rejectCallback: (value: Task) => setTaskValue(value) }
          : {},
      }),
    );
    if (setTaskValue) {
      setTaskValue((dataSource) => ({
        ...dataSource,
        atRisk: !atRisk,
      }));
    }
  }, [dispatch, projectId, taskId, atRisk, setTaskValue]);

  const togglePrivate = useCallback(() => {
    dispatch(
      updateTaskPrivacySagaAction({
        taskId,
        private: !isPrivate,
        projectId,
        options: setTaskValue
          ? { rejectCallback: (value: Task) => setTaskValue(value) }
          : {},
      }),
    );
    if (setTaskValue) {
      setTaskValue((dataSource) => ({
        ...dataSource,
        private: !isPrivate,
      }));
    }
  }, [dispatch, taskId, isPrivate, projectId, setTaskValue]);

  const handleConvertToMilestone = useCallback(() => {
    dispatch(
      updateTaskSagaAction({
        projectId,
        taskId,
        data: {
          type:
            type === TaskType.Milestone ? TaskType.Task : TaskType.Milestone,
        },
        options: setTaskValue
          ? { rejectCallback: (value: Task) => setTaskValue(value) }
          : {},
      }),
    );
    if (setTaskValue) {
      setTaskValue((dataSource) => ({
        ...dataSource,
        type: type === TaskType.Milestone ? TaskType.Task : TaskType.Milestone,
      }));
    }
  }, [dispatch, projectId, taskId, type, setTaskValue]);

  const handleMoveTask = useCallback(
    async (projectPhase: ProjectPhase) => {
      const {
        projectPhaseId,
        startDate: phaseStartDate,
        dueDate: phaseDueDate,
      } = projectPhase;

      if (
        isTaskDatesOutOfPhaseDateRange(
          [startDate, dueDate],
          [phaseStartDate, phaseDueDate],
        )
      ) {
        setMoveTaskState((prevState) => ({
          ...prevState,
          isModalVisible: true,
          projectPhase,
        }));
      } else {
        await handleMoveTaskSubmit({ projectPhase: { projectPhaseId } });
      }
    },
    [startDate, dueDate, handleMoveTaskSubmit],
  );

  return useMemo(
    () => ({
      handleMarkAtRisk,
      handleMoveTask,
      handleConvertToMilestone,
      togglePrivate,
      moveTaskState,
      handleMoveTaskSubmit,
      handleMoveTaskCancel,
      isUpdateTaskPhaseLoading,
      deleteConfirmation,
    }),
    [
      handleMarkAtRisk,
      handleMoveTask,
      handleConvertToMilestone,
      togglePrivate,
      moveTaskState,
      handleMoveTaskSubmit,
      handleMoveTaskCancel,
      isUpdateTaskPhaseLoading,
      deleteConfirmation,
    ],
  );
};
